import type { Message, Thread } from '../services/chatService';
import { recalledVehicleThreadId } from './vehicleThreadMemory';

type VehicleInquiryRef = {
  topicKey: string;
  vehicleStock?: string;
  vehicleId?: string;
};

/** Open, pending, or snoozed — a finished/hidden thread must not be reused. */
const REUSABLE_CHAT_STATES = new Set(['open', 'pending', 'snoozed']);

export function isReusableChatThread(thread: Pick<Thread, 'state'>): boolean {
  const state = String(thread.state || 'open').toLowerCase();
  return REUSABLE_CHAT_STATES.has(state);
}

function parseActivityTime(value?: string | number): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const raw = String(value).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    return n < 1e12 ? n * 1000 : n;
  }
  const n = new Date(raw).getTime();
  return Number.isNaN(n) ? 0 : n;
}

function threadActivityTime(thread: Thread): number {
  return (
    parseActivityTime(thread.last_message?.timestamp) ||
    parseActivityTime((thread as Thread & { updatedAt?: string }).updatedAt) ||
    0
  );
}

function pickMostRecent(matches: Thread[]): Thread | undefined {
  if (matches.length === 0) return undefined;
  const active = matches.filter(isReusableChatThread);
  const pool = active.length > 0 ? active : matches;
  return [...pool].sort((a, b) => threadActivityTime(b) - threadActivityTime(a))[0];
}

/** Most recent open (or snoozed) app thread for a service topic category. */
export function findExistingTopicThread(threads: Thread[], topicKey: string): Thread | undefined {
  return pickMostRecent(threads.filter((t) => t.category === topicKey));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function vehicleNeedleRe(value: string): RegExp {
  return new RegExp(`(?:^|[^a-z0-9])#?${escapeRegExp(value)}(?:[^a-z0-9]|$)`, 'i');
}

function isGenericVehicleThreadTitle(title: string | undefined): boolean {
  const normalized = (title || '').trim().toLowerCase();
  return normalized === 'cwy support' || normalized === 'buy a vehicle';
}

function blobHasVehicle(blob: string, stockRe: RegExp | null, idRe: RegExp | null): boolean {
  if (stockRe && stockRe.test(blob)) return true;
  if (idRe && idRe.test(blob)) return true;
  return false;
}

function threadMatchesVehicle(
  thread: Thread,
  topicKey: string,
  stockRe: RegExp | null,
  idRe: RegExp | null
): boolean {
  if (!isReusableChatThread(thread)) return false;
  if (thread.category && thread.category !== topicKey) return false;
  const title = thread.title || '';
  const tags = (thread.tags || []).join(' ');
  const first = thread.first_message?.content || '';
  const last = thread.last_message?.content || '';

  if (blobHasVehicle(title, stockRe, idRe)) return true;
  if (blobHasVehicle(tags, stockRe, idRe)) return true;
  // Original inquiry still identifies the car after a staff reply replaces last_message.
  if (blobHasVehicle(first, stockRe, idRe)) return true;
  // Generic Support / Buy a Vehicle dumps mix cars — never match on last_message alone.
  if (isGenericVehicleThreadTitle(title)) return false;
  if (blobHasVehicle(last, stockRe, idRe)) return true;
  return false;
}

/** Reopen the thread for one vehicle (stock # / vehicle id), not a generic Buy a Vehicle / Support chat. */
export function findExistingVehicleThread(
  threads: Thread[],
  topicKey: string,
  stockNumber: string,
  vehicleId?: string,
  rememberedThreadId?: string
): Thread | undefined {
  if (rememberedThreadId) {
    const remembered = threads.find(
      (t) =>
        isReusableChatThread(t) &&
        (t.thread_id === rememberedThreadId || t._id === rememberedThreadId)
    );
    if (remembered) return remembered;
  }
  const stock = stockNumber.trim().toLowerCase();
  const id = vehicleId?.trim().toLowerCase() ?? '';
  if (!stock && !id) return undefined;
  const stockRe = stock ? vehicleNeedleRe(stock) : null;
  const idRe = id ? vehicleNeedleRe(id) : null;
  return pickMostRecent(threads.filter((t) => threadMatchesVehicle(t, topicKey, stockRe, idRe)));
}

export function findRememberedVehicleThread(
  threads: Thread[],
  clientId: string | undefined,
  topicKey: string,
  stockNumber: string,
  vehicleId?: string
): Thread | undefined {
  return findExistingVehicleThread(
    threads,
    topicKey,
    stockNumber,
    vehicleId,
    recalledVehicleThreadId(clientId, stockNumber, vehicleId)
  );
}

/** Fill first_message from thread history so same-car resume works after a staff reply. */
export async function hydrateVehicleThreadOrigins(
  threads: Thread[],
  _topicKey: string,
  fetchMessages: (threadId: string) => Promise<Message[]>
): Promise<Thread[]> {
  const pool = threads
    .filter((t) => isReusableChatThread(t) && !t.first_message?.content)
    .sort((a, b) => threadActivityTime(b) - threadActivityTime(a))
    .slice(0, 15);
  if (pool.length === 0) return threads;
  const extras = await Promise.all(
    pool.map(async (thread) => {
      try {
        const messages = await fetchMessages(thread.thread_id);
        const ordered = [...messages].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        const blob = ordered
          .map((m) => m.content || '')
          .filter(Boolean)
          .join('\n');
        return { thread_id: thread.thread_id, content: blob };
      } catch {
        return { thread_id: thread.thread_id, content: '' };
      }
    })
  );
  const byId = new Map(extras.map((row) => [row.thread_id, row.content]));
  return threads.map((thread) => {
    const content = byId.get(thread.thread_id);
    if (!content) return thread;
    return { ...thread, first_message: { content } };
  });
}

export function isSameVehicleInquiry(
  current: VehicleInquiryRef | null | undefined,
  next: VehicleInquiryRef
): boolean {
  if (!current || current.topicKey !== next.topicKey) return false;
  const stockA = current.vehicleStock?.trim().toLowerCase();
  const stockB = next.vehicleStock?.trim().toLowerCase();
  if (stockA && stockB && stockA === stockB) return true;
  const idA = current.vehicleId?.trim().toLowerCase();
  const idB = next.vehicleId?.trim().toLowerCase();
  return Boolean(idA && idB && idA === idB);
}

export function vehicleThreadTags(stockNumber?: string, vehicleId?: string): string[] {
  const tags: string[] = [];
  const stock = stockNumber?.trim();
  const id = vehicleId?.trim();
  if (stock) tags.push(`vehicle_stock:${stock}`);
  if (id) tags.push(`vehicle_id:${id}`);
  return tags;
}
